import { createSelector } from '@reduxjs/toolkit'
import defaultsDeep from 'lodash/defaultsDeep'
import cloneDeep from 'lodash/cloneDeep'
import pickBy from 'lodash/pickBy'
import reduce from 'lodash/reduce'
import each from 'lodash/each'
import { selectUsers } from 'selectors/users'

type GraphEdgeMap = import('types/domain').GraphEdgeMap
type GraphEdge = import('types/domain').GraphEdge
type NodeEdgeMap = import('types/domain').NodeEdgeMap
type UserMap = import('types/domain').UserMap
type RootState = import('types/state').RootState

interface RouteProps {
  params?: {
    nodeId?: string
    deleted?: boolean
  }
}

interface EdgeEntity {
  id: string
  deleted?: boolean
  data: {
    readers?: string[]
    writers?: string[]
    creator?: unknown
  }
}


const selectEdges = (state: RootState): GraphEdgeMap => {
  return state.graph?.edges?.all || {}
}

const selectNodeId = (_state: RootState, props?: RouteProps): string | undefined => {
  return props?.params?.nodeId
}

const selectIncludeDeleted = (_state: RootState, props?: RouteProps): boolean => {
  return Boolean(props?.params?.deleted)
}

export const byNodeMap = (state: RootState): NodeEdgeMap => {
  return state.graph?.edges?.byNode || {}
}

export const all = createSelector(selectEdges, selectIncludeDeleted, selectUsers, (edges: GraphEdgeMap, includeDeleted: boolean, users: UserMap) => {
  const filtered = pickBy(edges, (e: EdgeEntity) => includeDeleted || !e.deleted) as GraphEdgeMap
  each(filtered, (e: EdgeEntity, id: string) => {
    const updated = cloneDeep(e)
    updated.data.readers = reduce(
      e.data.readers ?? [],
      (rs: UserMap, rId: string) => {
        rs[rId] = defaultsDeep(users[rId], { id: rId })
        return rs
      },
      {} as UserMap
    )
    updated.data.writers = reduce(
      e.data.writers ?? [],
      (ws: UserMap, wId: string) => {
        ws[wId] = defaultsDeep(users[wId], { id: wId })
        return ws
      },
      {} as UserMap
    )
    filtered[id] = updated
    const creatorId = e.data.creator as string | null | undefined
    const creatorUser = creatorId ? users[creatorId] : undefined
    updated.data.creator = defaultsDeep(creatorUser, { id: creatorId })
  })
  return filtered
})

export const selectEdgesForNode = createSelector(selectNodeId, all, byNodeMap, selectIncludeDeleted, (nodeId: string | undefined, edges: GraphEdgeMap, nodeEdgeMap: NodeEdgeMap, _includeDeleted: boolean) => {
  if (!nodeId) {
    return []
  }
  const eMap = nodeEdgeMap[nodeId] || {}
  return reduce(
    eMap,
    (es: GraphEdge[], _meta: unknown, eId: string) => {
      if (edges[eId]) {
        return es.concat(edges[eId])
      }
      return es
    },
    [] as GraphEdge[]
  )
})

export default {
  all,
  byNodeMap,
  selectEdgesForNode
}
